import * as Yup from 'yup';

import {
  BoxLayout,
  DialogLayout,
  FormLoadingButton,
  FormSearchableSelectField,
  NewConvergeButton,
} from '../../../presentation';
import { FormProvider, useForm } from 'react-hook-form';
import {
  SubmissionStatus,
  SubmissionSubStatus,
  useUpsertSubmissionMutation,
} from '@converge/graphql';
import { useCallback, useState } from 'react';
import { useConvergeNavigation, useConvergeSnackBar } from '../../../contexts';

import { ComponentProps } from 'react';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { useUnderwritingSubmissionStateContext } from '../underwriting.submission.context';
import { yupResolver } from '@hookform/resolvers/yup';

export const SUBMISSION_LOST_REASON_OPTIONS = [
  {
    value: SubmissionSubStatus.LostPricing,
    label: 'Pricing',
  },
  {
    value: SubmissionSubStatus.LostPolicyTermsCoverage,
    label: 'Terms Coverage',
  },
  {
    value: SubmissionSubStatus.LostCommission,
    label: 'Lost commission',
  },
  {
    value: SubmissionSubStatus.LostBrokerNotAppointed,
    label: 'Not appointed',
  },
  {
    value: SubmissionSubStatus.LostInsured,
    label: 'Lost insured',
  },
  {
    value: SubmissionSubStatus.LostRetention,
    label: 'Retention',
  },
  {
    value: SubmissionSubStatus.LostPremium,
    label: 'Premium',
  },
  {
    value: SubmissionSubStatus.LostNoAppetite,
    label: 'No business appetite',
  },
  {
    value: SubmissionSubStatus.LostNoAuthority,
    label: 'No Authority',
  },
  {
    value: SubmissionSubStatus.LostNoAffiliated,
    label: 'Not affiliated',
  },
  {
    value: SubmissionSubStatus.LostGoBare,
    label: 'Go bare',
  },
  {
    value: SubmissionSubStatus.LostReducedLimits,
    label: 'Reduced limits',
  },
  {
    value: SubmissionSubStatus.LostWholesaleRetailRelationship,
    label: 'Wholesale to retail',
  },
  {
    value: SubmissionSubStatus.LostRetailToRetail,
    label: 'Retail to retail',
  },
  {
    value: SubmissionSubStatus.LostWholesaleToWholesale,
    label: 'Wholesale to wholesale',
  },
  {
    value: SubmissionSubStatus.LostQuotingExcess,
    label: 'Quoting excess',
  },
  {
    value: SubmissionSubStatus.LostAcquiredOrOutOfBusiness,
    label: 'Acquired or out of business',
  },
  {
    value: SubmissionSubStatus.LostRenewal,
    label: 'Lost renewal',
  },
];

interface ILostSubmissionButtonProps
  extends ComponentProps<typeof LoadingButton> {
  referenceId?: string;
  submissionId: string;
}
interface ILostFormFelids {
  reason: SubmissionSubStatus;
}
export const LostSubmissionButton = ({
  referenceId,
  submissionId,
  ...props
}: ILostSubmissionButtonProps) => {
  const {
    info: { allowLostSubmission },
  } = useUnderwritingSubmissionStateContext();

  const { notify, closeSnackbar } = useConvergeSnackBar();
  const navigate = useConvergeNavigation();
  const { upsertSubmission, loading: isUpdatingSubmission } =
    useUpsertSubmissionMutation({}, 'Overview');
  const [showLostConfirmationModal, setShowLostConfirmationModal] =
    useState<boolean>(false);
  const { t } = useTranslation();

  const onLostSubmissionConfirmationEvent = useCallback(
    async (submissionUuid: string, subStatus: SubmissionSubStatus) => {
      closeSnackbar();
      setShowLostConfirmationModal(false);

      try {
        await upsertSubmission({
          variables: {
            details: {
              uuid: submissionUuid,
              status: SubmissionStatus.Lost,
              subStatus,
            },
          },
        });
        notify('Saved successfully', {
          variant: 'success',
        });
        navigate.underwriting();
      } catch (cause) {
        closeSnackbar();
        notify(
          cause instanceof Error
            ? `Error saving: ${cause.message}`
            : 'Error saving',
          {
            variant: 'error',
          }
        );
      }
    },
    [closeSnackbar, navigate, notify, upsertSubmission]
  );

  const formMethods = useForm<ILostFormFelids>({
    resolver: yupResolver(
      Yup.object().shape({
        reason: Yup.string()
          .typeError(
            t<string>(
              'submission.lostSubmission.requiredError',
              'Prior to confirming, choose a reason'
            )
          )
          .required(
            t<string>(
              'submission.lostSubmission.requiredError',
              'Prior to confirming, choose a reason'
            )
          )
          .trim()
          .min(
            1,
            t<string>(
              'submission.lostSubmission.requiredError',
              'Prior to confirming, choose a reason'
            )
          ),
      })
    ),
  });

  if (!allowLostSubmission) {
    return null;
  }

  return (
    <>
      <NewConvergeButton
        {...props}
        loading={isUpdatingSubmission}
        variant="contained"
        onClick={() => setShowLostConfirmationModal(true)}
        label="Lost"
      />

      <DialogLayout
        id="lost-submission-dialog"
        open={showLostConfirmationModal}
        showCloseButton
        onCloseIconClick={() => setShowLostConfirmationModal(false)}
        onClose={() => setShowLostConfirmationModal(false)}
        maxWidth="lg"
        header={<>{t('submission.lostSubmission.header', 'Lost Submission')}</>}
        bodyText={t<string>(
          'submission.lostSubmission.bodyText',
          'Are you sure you want to change status of submission to Lost?',
          {
            referenceId,
          }
        )}
        body={
          <FormProvider {...formMethods}>
            <FormSearchableSelectField
              minDropdownWidth="650px"
              maxDropdownWidth="650px"
              name="reason"
              label="Reason"
              containerWidth={4}
              options={SUBMISSION_LOST_REASON_OPTIONS}
              required
              disabled={isUpdatingSubmission}
            />
          </FormProvider>
        }
        footer={
          <BoxLayout sx={{ m: 2 }}>
            <FormLoadingButton
              type="reset"
              onClick={() => setShowLostConfirmationModal(false)}
              sx={{ mr: 2 }}
              variant="text"
            >
              {t('submission.lostSubmission.cancel', 'Cancel')}
            </FormLoadingButton>
            <FormLoadingButton
              loading={isUpdatingSubmission}
              onClick={formMethods.handleSubmit((details) => {
                onLostSubmissionConfirmationEvent(
                  submissionId,
                  details.reason as unknown as SubmissionSubStatus
                );
              })}
            >
              {t('submission.lostSubmission.confirm', 'Confirm')}
            </FormLoadingButton>
          </BoxLayout>
        }
      />
    </>
  );
};
